import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, User, MapPin, Star, Mail, Phone, MessageCircle, FileText, Download, Lock } from "lucide-react";
import { Button } from "./Button";
import { Badge } from "./badge";
import { QuoteStatusEnum, PurchaseQuoteTypeEnum } from "../../modules/shared/model/quote.enum";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "./alert";

interface QuoteDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  quote: any;
}

const QuoteDetailModal: React.FC<QuoteDetailModalProps> = ({
  isOpen,
  onClose,
  quote
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  if (!isOpen || !quote) return null;
  const getStatusInfo = (status: any) => {
    if (typeof status === "object" && status?.id) {
      const statusMap = {
        [QuoteStatusEnum.Pending]: { text: t("quoteDetailModal.statusBadges.pending"), style: "bg-yellow-100 text-yellow-800" },
        [QuoteStatusEnum.Approved]: { text: t("quoteDetailModal.statusBadges.approved"), style: "bg-blue-100 text-blue-800" },
        [QuoteStatusEnum.Rejected]: { text: t("quoteDetailModal.statusBadges.rejected"), style: "bg-red-100 text-red-800" },
        [QuoteStatusEnum.Used]: { text: t("quoteDetailModal.statusBadges.used"), style: "bg-green-100 text-green-800" },
        [QuoteStatusEnum.Completed]: { text: t("quoteDetailModal.statusBadges.completed"), style: "bg-green-100 text-green-800" },
        [QuoteStatusEnum.Active]: { text: t("quoteDetailModal.statusBadges.active"), style: "bg-purple-100 text-purple-800" },
        [QuoteStatusEnum.InActive]: { text: t("quoteDetailModal.statusBadges.inactive"), style: "bg-gray-100 text-gray-800" }
      };
      return statusMap[status.id] || { text: t("quoteDetailModal.statusBadges.unknown"), style: "bg-gray-100 text-gray-800" };
    }
    return { text: t("quoteDetailModal.statusBadges.unknown"), style: "bg-gray-100 text-gray-800" };
  };

  const getPurchaseTypeText = (purchaseType: any) => {
    if (typeof purchaseType === "object" && purchaseType?.id) {
      const typeMap = {
        [PurchaseQuoteTypeEnum.Purchase]: t("quoteDetailModal.purchaseTypes.purchased"),
        [PurchaseQuoteTypeEnum.Credit]: t("quoteDetailModal.purchaseTypes.credits"),
        [PurchaseQuoteTypeEnum.Assign]: t("quoteDetailModal.purchaseTypes.assigned")
      };
      return typeMap[purchaseType.id] || t("quoteDetailModal.unknown");
    }
    return t("quoteDetailModal.unknown");
  };

  // Function to mask private information for Assign type quotes
  const maskPrivateInfo = (info: any, type: string) => {
    // If quote is purchased (not Assign type), show unmasked information
    if (quote && Number(quote.purchaseType?.id) !== PurchaseQuoteTypeEnum.Assign) {
      return info || "N/A";
    }

    // Handle null, undefined, or empty strings
    if (!info || typeof info !== "string" || info.trim() === "") {
      return "N/A";
    }

    switch (type) {
      case "name":
        return info.replace(/(?!^.).(?!$)/g, "*");
      case "email": {
        if (!info.includes("@")) {
          return "N/A";
        }
        const [localPart, domain] = info.split("@");
        if (!localPart || !domain || !domain.includes(".")) {
          return "N/A";
        }
        const domainParts = domain.split(".");
        return `${localPart[0]}***@${domainParts[0][0]}***.${domainParts[1] || "com"}`;
      }
      case "phone":
        return info.replace(/\d(?=\d{4})/g, "*");
      case "address":
        return info.replace(/\d+|[a-zA-Z]+(?= )/g, "***");
      default:
        return info;
    }
  };

  const StatusBadge = ({ status }: { status: any }) => {
    const statusInfo = getStatusInfo(status);
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.style}`}>
        {statusInfo.text}
      </span>
    );
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">{t("quoteDetailModal.title")}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="p-2"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Alert for Assign type quotes */}
            {Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Assign && (
              <Alert className="bg-yellow-50 border-yellow-200">
                <Lock className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-700">
                  {t("quoteDetailModal.assignedAlert")}
                </AlertDescription>
              </Alert>
            )}

            {/* Quote Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">{t("quoteDetailModal.quoteInformation")}</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">{t("quoteDetailModal.quoteId")}:</span>
                  <p className="font-medium">{quote.id}</p>
                </div>
                <div>
                  <span className="text-gray-600">{t("quoteDetailModal.amount")}:</span>
                  <p className="font-medium">${quote.amount?.toFixed(2) || "0.00"}</p>
                </div>
                <div>
                  <span className="text-gray-600">{t("quoteDetailModal.purchaseType")}:</span>
                  <p className="font-medium">{getPurchaseTypeText(quote.purchaseType)}</p>
                </div>
                <div>
                  <span className="text-gray-600">{t("quoteDetailModal.status")}:</span>
                  <StatusBadge status={quote.status} />
                </div>
                <div>
                  <span className="text-gray-600">{t("quoteDetailModal.created")}:</span>
                  <p className="font-medium">{new Date(quote.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-600">{t("quoteDetailModal.updated")}:</span>
                  <p className="font-medium">{new Date(quote.updatedAt).toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Job Information */}
            {quote.job && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{t("quoteDetailModal.jobInformation")}</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">{t("quoteDetailModal.jobTitle")}:</span>
                    <p className="font-medium">{quote.job.title}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">{t("quoteDetailModal.description")}:</span>
                    <p className="font-medium">{quote.job.description || t("quoteDetailModal.noDescription")}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">{t("quoteDetailModal.jobId")}:</span>
                    <p className="font-medium">{quote.job.id}</p>
                  </div>
                </div>
                
                {/* Job Files */}
                {quote.job.files && quote.job.files.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <h5 className="font-medium text-gray-900 text-sm mb-3">{t("quoteDetailModal.attachedFiles")}</h5>
                    <div className="space-y-2">
                      {quote.job.files.map((file: any, index: number) => (
                        <div key={index} className="flex items-center gap-3 p-2 bg-white rounded border">
                          <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file.originalName || file.name || `${t("quoteDetailModal.file")} ${index + 1}`}
                            </p>
                            {file.size && (
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            )}
                          </div>
                          {file.path && (
                            <a
                              href={file.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors"
                            >
                              <Download className="h-3 w-3" />
                              {t("quoteDetailModal.download")}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Client Information */}
            {quote.job?.userId && (
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{t("quoteDetailModal.clientInformation")}</h4>
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                    {(() => {
                      const client = quote.job.userId;
                      const photoUrl = client?.photo?.path || client?.photo?.url || client?.profilePhoto || "";
                      const name = (client?.firstName && client?.lastName) ? `${client.firstName} ${client.lastName}` : (client?.firstName || client?.name || "Client");
                      return photoUrl ? (
                        <img src={photoUrl} alt={name} className="h-12 w-12 rounded-full object-cover" />
                      ) : (
                        <User className="h-6 w-6 text-gray-600" />
                      );
                    })()}
                  </div>
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {maskPrivateInfo(
                          `${quote.job.userId?.firstName || ""} ${quote.job.userId?.lastName || ""}`.trim() || "Client",
                          "name"
                        )}
                      </span>
                      {quote.job.userId?.verified && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">{t("quoteDetailModal.verified")}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span>{quote.job.userId?.averageRating || 0}</span>
                      </div>
                      {quote.job.userId?.reviewsCount && (
                        <span>({quote.job.userId.reviewsCount} {t("quoteDetailModal.reviews")})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{maskPrivateInfo(quote.job.userId?.location || quote.job.userId?.address || t("quoteDetailModal.locationNotSpecified"), "address")}</span>
                    </div>
                    
                    {/* Contact Information */}
                    <div className="space-y-2 pt-2 border-t border-green-200">
                      <h5 className="font-medium text-gray-900 text-sm">{t("quoteDetailModal.contactInformation")}</h5>
                      <div className="space-y-2">
                        {quote.job.userId?.email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4 text-gray-500" />
                            <span className="text-gray-600">{t("quoteDetailModal.email")}:</span>
                            {Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Assign ? (
                              <span className="text-gray-600">{maskPrivateInfo(quote.job.userId.email, "email")}</span>
                            ) : (
                              <a 
                                href={`mailto:${quote.job.userId.email}`}
                                className="text-blue-600 hover:text-blue-800 underline"
                              >
                                {quote.job.userId.email}
                              </a>
                            )}
                          </div>
                        )}
                        {quote.job.userId?.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-4 w-4 text-gray-500" />
                            <span className="text-gray-600">{t("quoteDetailModal.phone")}:</span>
                            {Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Assign ? (
                              <span className="text-gray-600">{maskPrivateInfo(quote.job.userId.phone, "phone")}</span>
                            ) : (
                              <a 
                                href={`tel:${quote.job.userId.phone}`}
                                className="text-blue-600 hover:text-blue-800 underline"
                              >
                                {quote.job.userId.phone}
                              </a>
                            )}
                          </div>
                        )}
                        {quote.job.userId?.whatsapp && (
                          <div className="flex items-center gap-2 text-sm">
                            <MessageCircle className="h-4 w-4 text-green-600" />
                            <span className="text-gray-600">{t("quoteDetailModal.whatsapp")}:</span>
                            {Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Assign ? (
                              <span className="text-gray-600">{maskPrivateInfo(quote.job.userId.whatsapp, "phone")}</span>
                            ) : (
                              <a 
                                href={`https://wa.me/${quote.job.userId.whatsapp.replace(/[^\d]/g, "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-800 underline"
                              >
                                {quote.job.userId.whatsapp}
                              </a>
                            )}
                          </div>
                        )}
                        {!quote.job.userId?.email && !quote.job.userId?.phone && !quote.job.userId?.whatsapp && (
                          <p className="text-sm text-gray-500 italic">{t("quoteDetailModal.noContactInfo")}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Escrow Information */}
            {quote.escrow && (
              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{t("quoteDetailModal.paymentInformation")}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">{t("quoteDetailModal.escrowId")}:</span>
                    <p className="font-medium">{quote.escrow.id}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">{t("quoteDetailModal.escrowAmount")}:</span>
                    <p className="font-medium">${quote.escrow.amount?.toFixed(2) || "0.00"}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">{t("quoteDetailModal.escrowStatus")}:</span>
                    <p className="font-medium">{quote.escrow.status?.name || t("quoteDetailModal.unknown")}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t">
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={onClose}
              >
                {t("quoteDetailModal.close")}
              </Button>
              <Button
                onClick={() => {
                  // Handle action like contact client
                  window.location.href = "/MessageAndVideoCall?professionalId=" + quote.job?.userId?.id + "&professionalName=" + quote.job?.userId?.firstName + " " + quote.job?.userId?.lastName;
                  // navigate("/MessageAndVideoCall?professionalId=" + quote.job?.userId?.id + "&professionalName=" + quote.job?.userId?.firstName + " " + quote.job?.userId?.lastName);
                  console.log("Contact client:", quote.job?.userId?.id);
                }}
              >
                {t("quoteDetailModal.contactClient")}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default QuoteDetailModal;
